/**
 * anonInterceptors.ts
 * Axios interceptors for anonymous edit token management.
 *
 * Request interceptor:
 * - Attaches `X-Anon-Edit-Token` to guarded mutation routes
 *
 * Response interceptor:
 * - Saves newly issued or rotated anonymous edit tokens from server responses
 */

import { AxiosHeaders, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { saveAnonToken, loadAnonToken, type AnonEntity } from "./anonTokenVault";

const POST_BACKED_ANON_ENTITIES: readonly AnonEntity[] = [
  "impact",
  "teabate",
  "solution",
  "action",
  "feedback",
  "collection",
];

type TokenCarrier = {
  id: number | string;
  token: string;
  typeHint: AnonEntity | null;
};

/**
 * Treat only plain objects as token carriers/search nodes.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Normalize URLs from relative or absolute Axios configs to pathnames.
 */
function getPathname(url: string): string {
  try {
    return new URL(url, "https://local.invalid").pathname;
  } catch {
    return url;
  }
}

/**
 * Normalize a route/entity label to the singular anon vault key.
 */
function normalizeAnonEntity(value: string | undefined): AnonEntity | null {
  if (!value) {
    return null;
  }

  switch (value.trim().toLowerCase()) {
    case "impact":
    case "impacts":
      return "impact";
    case "teabate":
    case "teabates":
      return "teabate";
    case "solution":
    case "solutions":
      return "solution";
    case "action":
    case "actions":
      return "action";
    case "feedback":
    case "feedbacks":
      return "feedback";
    case "collection":
    case "collections":
      return "collection";
    case "comment":
    case "comments":
      return "comment";
    default:
      return null;
  }
}

/**
 * Check if HTTP method is a mutation.
 */
function isMutation(method?: string): boolean {
  const value = (method || "get").toLowerCase();
  return value === "post" || value === "patch" || value === "delete" || value === "put";
}

/**
 * Unified create stays token-free because the entity ID does not exist yet.
 */
function isUnifiedPostCreateUrl(url: string): boolean {
  return /^\/api\/posts\/?$/.test(getPathname(url));
}

/**
 * Resolve concrete entity routes like `/api/entities/action/123/media`.
 */
function extractEntityRouteTarget(url: string): { type: AnonEntity; id: string } | null {
  const match = getPathname(url).match(/^\/api\/entities\/([^/]+)\/(\d+)(?:\/|$)/);
  if (!match) {
    return null;
  }

  const type = normalizeAnonEntity(match[1]);
  return type ? { type, id: match[2] } : null;
}

/**
 * Resolve direct unified post mutations like `/api/posts/123`.
 */
function extractUnifiedPostId(url: string): string | null {
  const match = getPathname(url).match(/^\/api\/posts\/(\d+)\/?$/);
  return match?.[1] ?? null;
}

/**
 * Resolve legacy direct entity routes that still use concrete resource names.
 */
function extractLegacyEntityRouteTarget(url: string): { type: AnonEntity; id: string } | null {
  const match = getPathname(url).match(/^\/api\/(comments|impacts|teabates|solutions|feedbacks|actions)\/(\d+)(?:\/|$)/);
  if (!match) {
    return null;
  }

  const type = normalizeAnonEntity(match[1]);
  return type ? { type, id: match[2] } : null;
}

/**
 * Read the first post-backed token that matches a unified post ID.
 * Post-backed IDs live in the same `posts` table, so only one concrete kind
 * should match for a given ID.
 */
async function loadUnifiedPostToken(id: string): Promise<string | null> {
  for (const type of POST_BACKED_ANON_ENTITIES) {
    const token = await loadAnonToken(type, id);
    if (token) {
      return token;
    }
  }

  return null;
}

/**
 * Attach the anon edit token header using Axios' native header wrapper.
 */
function setAnonEditTokenHeader(config: InternalAxiosRequestConfig, token: string): void {
  const headers = AxiosHeaders.from(config.headers);
  headers.set("X-Anon-Edit-Token", token);
  config.headers = headers;
}

/**
 * Read a token carrier from a response payload.
 * Supports top-level entity responses and nested wrappers like `{ entity: {...} }`
 * or `{ comment: {...} }`.
 */
function findTokenCarrier(data: unknown): TokenCarrier | null {
  if (!isRecord(data)) {
    return null;
  }

  const queue: Record<string, unknown>[] = [data];
  while (queue.length > 0) {
    const candidate = queue.shift();
    if (!candidate) {
      continue;
    }

    const token = typeof candidate.anon_edit_token === "string" ? candidate.anon_edit_token : null;
    const id =
      typeof candidate.id === "number" || typeof candidate.id === "string"
        ? candidate.id
        : null;

    if (token && id !== null) {
      const kind = typeof candidate.kind === "string" ? candidate.kind : undefined;
      return {
        id,
        token,
        typeHint: normalizeAnonEntity(kind),
      };
    }

    for (const value of Object.values(candidate)) {
      if (isRecord(value)) {
        queue.push(value);
      }
    }
  }

  return null;
}

/**
 * Install anonymous token interceptors on an Axios instance.
 */
export function installAnonInterceptors(axios: AxiosInstance): void {
  axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const url = config.url || "";
    if (!isMutation(config.method)) {
      return config;
    }

    if (isUnifiedPostCreateUrl(url) && (config.method || "").toLowerCase() === "post") {
      return config;
    }

    const entityTarget = extractEntityRouteTarget(url);
    if (entityTarget) {
      const token = await loadAnonToken(entityTarget.type, entityTarget.id);
      if (token) {
        setAnonEditTokenHeader(config, token);
      }
      return config;
    }

    const unifiedPostId = extractUnifiedPostId(url);
    if (unifiedPostId) {
      const token = await loadUnifiedPostToken(unifiedPostId);
      if (token) {
        setAnonEditTokenHeader(config, token);
      }
      return config;
    }

    const legacyTarget = extractLegacyEntityRouteTarget(url);
    if (legacyTarget) {
      const token = await loadAnonToken(legacyTarget.type, legacyTarget.id);
      if (token) {
        setAnonEditTokenHeader(config, token);
      }
    }

    return config;
  });

  axios.interceptors.response.use(async (resp: AxiosResponse) => {
    const url = resp.config.url || "";
    const hintedType =
      extractEntityRouteTarget(url)?.type
      ?? extractLegacyEntityRouteTarget(url)?.type
      ?? null;

    const carrier = findTokenCarrier(resp.data);
    if (!carrier) {
      return resp;
    }

    const type = carrier.typeHint ?? hintedType;
    if (type) {
      await saveAnonToken(type, carrier.id, carrier.token);
    }

    return resp;
  });
}

